import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useThemeStore } from '@/stores/use-theme-store';

const CODE_SNIPPETS = [
    'import { defineEntity } from "@sap/cds";',
    'entity PurchaseOrder @cds.autoExpose {',
    '  key ID : UUID;',
    '  poNumber : String(10);',
    '  vendor : Association to Vendor;',
    '}',
    '',
    'CLASS zcl_purchase_order DEFINITION PUBLIC.',
    '  METHODS: create_order IMPORTING iv_vendor TYPE bukrs.',
    'ENDCLASS.',
    '',
    'const mcpServer = new McpServer({',
    '  name: "abap-mcp",',
    '  transport: "stdio",',
    '});',
    '',
    '@UI.lineItem: [{ position: 10 }]',
    'annotate PurchaseOrder with {',
    '  poNumber @title: "PO Number";',
    '};',
    '',
    'SELECT * FROM ztable',
    '  WHERE bukrs = @company_code',
    '  INTO TABLE @DATA(lt_result).',
    '',
    'export function scaffoldCapProject({',
    '  name, namespace, features',
    '}: ProjectConfig) {',
    '  return generateFiles(template);',
    '}',
    '',
    'REPORT zgenerate_mcp_config.',
    'DATA: lv_api_key TYPE string.',
    'lv_api_key = zcl_mcp=>generate_key( ).',
    '',
    'const { data } = await apiService.get<ODataResponse>(',
    '  `/api/PurchaseOrders?$top=50`',
    ');',
    '',
    'service CatalogService {',
    '  entity Products as projection on db.Products;',
    '  action submitOrder(id: UUID): Boolean;',
    '}',
];

interface Line {
    text: string;
    y: number;
    opacity: number;
    speed: number;
    x: number;
    fontSize: number;
}

export function CodeBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const { mode } = useThemeStore();

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return; 
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return; 
        }

        let animationId: number;
        const lines: Line[] = [];

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        resize();
        window.addEventListener('resize', resize);

        // Initialize lines
        for (let i = 0; i < 30; i++) {
            lines.push({
                text: CODE_SNIPPETS[Math.floor(Math.random() * CODE_SNIPPETS.length)] ?? '',
                y: Math.random() * canvas.height,
                opacity: Math.random() * 0.15 + 0.03,
                speed: Math.random() * 0.3 + 0.1,
                x: Math.random() * canvas.width * 0.8 - canvas.width * 0.1,
                fontSize: Math.random() * 4 + 12,
            });
        }

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const textColor = mode === 'dark' ? '248, 250, 252' : '31, 41, 55';

            lines.forEach((line) => {
                ctx.font = `${line.fontSize}px "JetBrains Mono", monospace`;
                ctx.fillStyle = `rgba(${textColor}, ${line.opacity})`;
                ctx.fillText(line.text, line.x, line.y);

                line.y -= line.speed;

                if (line.y < -20) {
                    line.y = canvas.height + 20;
                    line.text = CODE_SNIPPETS[Math.floor(Math.random() * CODE_SNIPPETS.length)] ?? '';
                    line.x = Math.random() * canvas.width * 0.8 - canvas.width * 0.1;
                }
            });

            animationId = requestAnimationFrame(animate);
        };

        animate();

        return () => {
            cancelAnimationFrame(animationId);
            window.removeEventListener('resize', resize);
        };
    }, [mode]);

    return (
        <Box
            component="canvas"
            ref={canvasRef}
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
            }}
        />
    );
}

export default CodeBackground;
